import { fetchWithSupabaseAuth } from "./supabaseClient.js";

export const MISSING_TRACTOR_LABEL = "Camion à choisir";

export async function loadTractorCatalog({ dispatchDate = "", dispatchPeriod = "day", signal } = {}) {
  const query = new URLSearchParams({ dispatch_date: dispatchDate, dispatch_period: dispatchPeriod });
  const response = await fetchWithSupabaseAuth(`/api/planning/tractors?${query}`, { signal });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload?.detail?.message || payload?.detail || "Catalogue des camions indisponible.");
  return payload;
}

function withDriver(name, driverName) {
  return [name, driverName].filter(Boolean).join(" · ");
}

// Camion d'une option : la remorque qui le tire et son chauffeur, seulement si cette remorque roule dans la période ;
// un camion qui ne tire rien est « Libre » (« camion seul · Libre » pour un camion sans remorque).
export function resolveTractorOptionParts(tractor = {}, { lines = [], truckId = "" } = {}) {
  const pulling = Boolean(tractor.used_by_truck_id) && tractor.used_by_active !== false;
  const holder = pulling && tractor.used_by_truck_id !== truckId
    ? lines.find((line) => line.truck_id === tractor.used_by_truck_id)
    : null;
  const freeLabel = pulling ? "" : tractor.unit_kind === "camion" ? "camion seul · Libre" : "Libre";
  return {
    name: tractor.tractor_name || tractor.tractor_truck_id || "",
    usedByLabel: holder ? withDriver(holder.trailer_label || holder.truck_id, holder.driver_name) : freeLabel,
  };
}

// « Changer de remorque » : les autres remorques ; une remorque sans camion actif est libre (« Camion à choisir »).
export function resolveTrailerOptions(catalog, truckId = "") {
  return (catalog?.lines || []).filter((line) => line.truck_id !== truckId).map((line) => ({
    id: line.truck_id,
    name: line.trailer_label || line.truck_id,
    usedByLabel: line.tractor_status === "missing"
      ? MISSING_TRACTOR_LABEL
      : withDriver(line.tractor_name || line.tractor_truck_id, line.driver_name),
    version: line.version,
    details: line,
  }));
}

// --- Lignes vides du Gantt (« Ajouter ») : une remorque sans camion ou un camion sans remorque, pour la journée affichée.

export const STANDALONE_UNITS_CHANGED_EVENT = "grain-standalone-units-changed";
export const STANDALONE_WAITING_LABELS = { remorque: MISSING_TRACTOR_LABEL, camion: "Remorque à choisir" };

function trailerMeta(line) {
  const compartments = Number(line?.compartment_count) || 0;
  return [Number.isFinite(Number(line?.capacity_tons)) ? `${Number(line.capacity_tons)} t` : "", compartments ? `${compartments} comp.` : ""]
    .filter(Boolean).join(" · ");
}

// Camions libres de la journée : « Libre », avec le chauffeur par défaut de la période (prérempli s'il est libre).
export function freeTractorOptions(catalog) {
  const tractors = new Map((catalog?.tractors || []).map((tractor) => [tractor.tractor_truck_id, tractor]));
  return (catalog?.free_tractor_ids || []).map((id) => {
    const tractor = tractors.get(id) || {};
    return { id, name: tractor.tractor_name || id, kind: tractor.unit_kind === "camion" ? "camion seul" : "",
      usedByLabel: withDriver("Libre", tractor.driver_name), details: tractor };
  });
}

// Vraies remorques sans camion de la journée, y compris celles qui ont des commandes (« À ajuster »).
export function waitingTrailerOptions(catalog) {
  const lines = new Map((catalog?.lines || []).map((line) => [line.truck_id, line]));
  return (catalog?.waiting_trailer_ids || []).map((id) => {
    const line = lines.get(id) || {};
    return { id, name: line.trailer_label || id, kind: line.unit_kind === "remorque" ? "remorque seule" : "",
      usedByLabel: trailerMeta(line), version: line.version, details: line };
  });
}

// « Ajouter » : les remorques pas encore au Gantt (ligne du plan ou ligne vide) et les camions libres sans ligne vide.
export function addableStandaloneUnits(catalog, ganttTruckIds = new Set()) {
  const units = catalog?.standalone_units || [];
  const hasUnit = (id, part) => units.some((unit) => unit.truck_id === id && unit.part === part);
  return {
    trailers: waitingTrailerOptions(catalog).filter((option) => !ganttTruckIds.has(option.id) && !hasUnit(option.id, "remorque")),
    tractors: freeTractorOptions(catalog).filter((option) => !hasUnit(option.id, "camion")),
  };
}

async function standaloneRequest(path, { method, body, fallback }) {
  const response = await fetchWithSupabaseAuth(path, {
    method, ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.detail?.message || (typeof payload?.detail === "string" ? payload.detail : "") || fallback);
  window.dispatchEvent(new CustomEvent(STANDALONE_UNITS_CHANGED_EVENT, { detail: payload }));
  return payload;
}

export function createStandaloneUnit({ dispatchDate, dispatchPeriod = "day", truckId, part }) {
  return standaloneRequest("/api/planning/standalone-units", { method: "POST", fallback: "Impossible d'ajouter cette ligne.",
    body: { dispatch_date: dispatchDate, dispatch_period: dispatchPeriod, truck_id: truckId, part } });
}

export function deleteStandaloneUnit(unitId) {
  return standaloneRequest(`/api/planning/standalone-units/${encodeURIComponent(unitId)}`, { method: "DELETE",
    fallback: "Impossible de retirer cette ligne." });
}

export function saveStandaloneUnitDriver(unitId, { employeeId = null, expectedVersion }) {
  return standaloneRequest(`/api/planning/standalone-units/${encodeURIComponent(unitId)}/driver`, { method: "PATCH",
    fallback: "Impossible d'enregistrer le chauffeur.", body: { employee_id: employeeId || null, expected_version: expectedVersion } });
}

export function tractorAssignmentChallenge(payload, status) {
  const detail = payload?.detail || payload;
  return status === 409 && detail?.code === "tractor_assignment_confirmation_required"
    && detail?.versions && detail?.holder_truck_id ? detail : null;
}

function challengeScope(challenge) {
  return { label: "Portée", value: `${challenge.dispatch_date} — ${challenge.dispatch_period === "evening" ? "Soir" : "Jour"}` };
}

const CONFIRMATION_STYLE = { cornerRadius: "grain", contentDensity: "compact", summaryDensity: "compact", tone: "accent" };

// « Changer de camion » : la remorque du menu reçoit un camion qui tire déjà une autre remorque.
export function tractorAssignmentConfirmation(challenge) {
  const holder = challenge.holder_truck_label || challenge.holder_truck_id;
  const target = challenge.truck_label || challenge.truck_id;
  const camion = challenge.tractor_name || challenge.tractor_truck_id;
  const canSwap = challenge.can_swap !== false;
  return {
    ...CONFIRMATION_STYLE,
    title: "Ce camion tire déjà une remorque",
    description: canSwap
      ? `Le ${camion} tire la ${holder}. Par défaut, les deux camions échangent leurs remorques, chacun avec son chauffeur.`
      : `Le ${camion} tire la ${holder}. La ${target} n'a pas de camion à donner : la ${holder} passera à « ${MISSING_TRACTOR_LABEL} ».`,
    summaryItems: [
      { label: `Prend la ${target}`, value: withDriver(camion, challenge.driver_name) },
      ...(canSwap ? [{ label: `Prend la ${holder}`, value: withDriver(challenge.replaced_tractor_name || "—", challenge.replaced_driver_name) }] : []),
      challengeScope(challenge),
    ],
    ...(canSwap ? { checkboxFields: [{ key: "leave_missing", label: `Laisser la ${holder} sans camion au lieu d'échanger` }] } : {}),
    confirmLabel: canSwap ? "Confirmer" : `Laisser la ${holder} sans camion`,
  };
}

// « Changer de remorque » : le camion du menu part, avec son chauffeur, sur une remorque déjà tirée ou sans camion.
export function trailerChangeConfirmation(challenge) {
  const source = challenge.holder_truck_label || challenge.holder_truck_id;
  const target = challenge.truck_label || challenge.truck_id;
  const camion = withDriver(challenge.tractor_name || challenge.tractor_truck_id, challenge.driver_name);
  const other = withDriver(challenge.replaced_tractor_name || "—", challenge.replaced_driver_name);
  const canSwap = challenge.can_swap !== false;
  return {
    ...CONFIRMATION_STYLE,
    title: canSwap ? "Cette remorque est déjà tirée" : "Cette remorque n'a pas de camion",
    description: canSwap
      ? `La ${target} est tirée par le ${other}. Par défaut, les deux camions échangent leurs remorques, chacun avec son chauffeur.`
      : `Le ${camion} prend la ${target} ; la ${source} passera à « ${MISSING_TRACTOR_LABEL} ».`,
    summaryItems: [
      { label: `Prend la ${target}`, value: camion },
      ...(canSwap ? [{ label: `Prend la ${source}`, value: other }] : []),
      challengeScope(challenge),
    ],
    ...(canSwap ? { checkboxFields: [{ key: "leave_missing", label: `Ne pas échanger : la ${source} reste sans camion` }] } : {}),
    confirmLabel: canSwap ? "Confirmer" : "Changer de remorque",
  };
}

export function confirmedTractorAssignment(body, challenge, leaveMissing = false) {
  const mode = challenge.can_swap === false || leaveMissing ? "leave_missing" : "swap";
  return { ...body, confirmation: { mode, versions: { ...challenge.versions } } };
}
